export const GUIDED_STORY_TEMPLATE = `
  <header class="site-header">
    <a class="brand" href="./" aria-label="Collaborative State Demos home">
      <span class="brand__mark" aria-hidden="true">CS</span>
      <span>
        <strong>Collaborative State Demos</strong>
        <small>Public alpha · Final feature milestone</small>
      </span>
    </a>
    <nav class="site-nav module-nav" aria-label="Demo modules">
      <a href="./">JSON Merge</a>
      <a href="?demo=semantic-ast">Validators + AST</a>
      <a href="?demo=tct-delivery">TCT Delivery</a>
      <a href="?demo=guided-story" aria-current="page">Guided Story</a>
      <a href="#story-evidence">Evidence</a>
    </nav>
  </header>

  <main id="top">
    <section class="hero story-hero" aria-labelledby="story-hero-title">
      <div class="hero__copy">
        <p class="eyebrow">Four independent drafts · one careful application story</p>
        <h1 id="story-hero-title">Discover.<br /><em>Protect.</em><br />Reconcile.</h1>
        <p class="hero__lede">
          Start with exact TCT bytes, keep three validator scopes distinct,
          reject one stale write, then reconcile only after a fresh state read.
        </p>
        <div class="hero__actions">
          <a class="button button--primary" href="#story-workbench">Begin the story</a>
          <a class="button button--quiet" href="?demo=tct-delivery">Review TCT delivery</a>
        </div>
      </div>
      <aside class="story-hero__map" aria-label="Four-draft guided story">
        <article data-accent="tct"><span>01 · TCT</span><strong>Discover exact bytes</strong><small>M-Sitemap → M-URL</small></article>
        <b aria-hidden="true">→</b>
        <article data-accent="semantic"><span>02 · Semantic</span><strong>Name logical state</strong><small>separate validator scope</small></article>
        <b aria-hidden="true">→</b>
        <article data-accent="ast"><span>03 · AST</span><strong>Protect the SBR</strong><small>If-Match → 412 → fresh GET</small></article>
        <b aria-hidden="true">→</b>
        <article data-accent="merge"><span>04 · Merge</span><strong>Reconcile locally</strong><small>complete value or conflicts</small></article>
      </aside>
    </section>

    <section class="boundary-note" aria-label="Guided story boundary">
      <strong>Informative composition only</strong>
      <span>No draft requires the others. No displayed request leaves the browser, and merge never grants approval or authority.</span>
    </section>

    <section class="section story-workbench" id="story-workbench" aria-labelledby="story-workbench-title">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Guided reconciliation workbench</p>
          <h2 id="story-workbench-title">Advance one governed transition at a time.</h2>
        </div>
        <label class="scenario-picker">
          <span>Fixed branch</span>
          <select id="story-branch-select" aria-describedby="story-branch-summary">
            <option value="merge">Non-overlapping edits merge</option>
            <option value="conflict">Concurrent title edits conflict</option>
          </select>
          <small id="story-branch-summary"></small>
        </label>
      </div>

      <section class="story-progress" aria-label="Cross-draft progress">
        <article id="story-progress-tct"><span>01</span><strong>TCT</strong><small>Waiting</small></article>
        <i aria-hidden="true">→</i>
        <article id="story-progress-semantic"><span>02</span><strong>Semantic</strong><small>Waiting</small></article>
        <i aria-hidden="true">→</i>
        <article id="story-progress-ast"><span>03</span><strong>AST</strong><small>Waiting</small></article>
        <i aria-hidden="true">→</i>
        <article id="story-progress-merge"><span>04</span><strong>Merge</strong><small>Waiting</small></article>
      </section>

      <section class="story-control-panel" aria-label="Story controls">
        <div>
          <p class="eyebrow">Active run <span id="story-run-id"></span></p>
          <h3 id="story-phase-label">Ready to begin</h3>
          <p id="story-phase-explanation"></p>
        </div>
        <div class="story-actions">
          <button class="button button--quiet" id="story-reset" type="button">Reset run</button>
          <button class="button button--primary" id="story-next" type="button">Verify TCT delivery</button>
          <button class="button button--primary story-confirm" id="story-confirm" type="button" hidden>Confirm and simulate one retry</button>
        </div>
      </section>

      <aside class="story-outcome" id="story-outcome" aria-live="polite">
        <span id="story-outcome-mode">Fixed application contract</span>
        <strong id="story-outcome-label">Ready · zero commits</strong>
        <p id="story-outcome-detail">The client has not observed or changed any state.</p>
        <small id="story-outcome-counts">0 client commits · 0 intervening commits</small>
      </aside>

      <section class="story-state-section" aria-labelledby="story-state-title">
        <div class="timeline-section__heading">
          <p class="eyebrow">Exact merge inputs</p>
          <h3 id="story-state-title">Base, source, and freshly retrieved target</h3>
        </div>
        <div class="story-state-grid">
          <article><span>Base · retained SBR</span><pre id="story-base-json"></pre></article>
          <article><span>Source · local only</span><pre id="story-source-json"></pre></article>
          <article><span>Target · current SBR</span><pre id="story-target-json"></pre></article>
        </div>
        <article class="story-engine-result" id="story-engine-result" data-state="idle">
          <div>
            <p class="eyebrow">Pinned worker response</p>
            <h3 id="story-engine-title">Not invoked</h3>
            <p id="story-engine-detail">The engine runs only after the current SBR target is retrieved.</p>
          </div>
          <pre id="story-engine-raw">No merge response.</pre>
        </article>
      </section>

      <section class="story-validator-section" aria-labelledby="story-validator-title">
        <div class="timeline-section__heading">
          <p class="eyebrow">Validator transition contract</p>
          <h3 id="story-validator-title">Same state does not mean same ETag.</h3>
        </div>
        <div class="story-table-wrap">
          <table class="story-validator-table">
            <thead><tr><th>State</th><th>SBR ETag</th><th>M-URL ETag</th><th>Semantic-ETag</th><th>Meaning</th></tr></thead>
            <tbody id="story-validator-rows"></tbody>
          </table>
        </div>
      </section>

      <section class="timeline-section" aria-labelledby="story-timeline-title">
        <div class="timeline-section__heading">
          <p class="eyebrow">Complete local chronology</p>
          <h3 id="story-timeline-title">Viewer knowledge and client knowledge stay separate.</h3>
        </div>
        <div class="state-timeline" id="story-timeline"></div>
      </section>
    </section>

    <section class="concepts state-principles section" aria-labelledby="story-principles-title">
      <div class="section-heading section-heading--compact">
        <div>
          <p class="eyebrow">Composition without scope inflation</p>
          <h2 id="story-principles-title">Four tools. Four jobs. One explicit boundary.</h2>
        </div>
      </div>
      <div class="concept-grid">
        <article><span>01</span><h3>Projection is not state authority</h3><p>The TCT M-URL is projected by default. The separate SBR exposes the complete state needed for reconciliation.</p></article>
        <article><span>02</span><h3>Validators keep their targets</h3><p>M-URL ETag, SBR ETag, and Semantic-ETag describe different scopes and are never substituted.</p></article>
        <article><span>03</span><h3>412 requires fresh knowledge</h3><p>A returned validator is advisory. The client retrieves current SBR state before attempting three-way merge.</p></article>
        <article><span>04</span><h3>Merged is not approved</h3><p>The complete candidate passes application checks and explicit simulation confirmation before one conditional retry.</p></article>
      </div>
    </section>

    <section class="evidence section" id="story-evidence" aria-labelledby="story-evidence-title">
      <div class="evidence__copy">
        <p class="eyebrow">Final feature milestone</p>
        <h2 id="story-evidence-title">Published drafts, frozen engine, local story.</h2>
        <p>Every transition is fixture-bound and non-normative. After this milestone the repository enters maintenance mode.</p>
      </div>
      <dl class="evidence-list">
        <div><dt>Specifications</dt><dd>TCT -03 · Semantic Validators -01 · AST -02 · JSON Merge -00</dd></div>
        <div><dt>Merge engine</dt><dd>libdualnative-wasm-v1 0.1.0-alpha.1 · bounded worker</dd></div>
        <div><dt>Mutation</dt><dd>simulated whole-resource PUT · one maximum client commit</dd></div>
        <div><dt>Network</dt><dd>packaged same-origin assets only · no displayed request is sent</dd></div>
      </dl>
    </section>
  </main>

  <footer class="site-footer">
    <p>Collaborative State Demos · non-normative, browser-only explanation</p>
    <div>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-collab-tunnel/03/" target="_blank" rel="noreferrer">TCT -03</a>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-http-semantic-validator/01/" target="_blank" rel="noreferrer">Semantic -01</a>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-httpapi-agentic-state/02/" target="_blank" rel="noreferrer">AST -02</a>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-json-three-way-merge/00/" target="_blank" rel="noreferrer">Merge -00</a>
      <a href="https://github.com/antunjurkovic-collab/collaborative-state-demos" target="_blank" rel="noreferrer">Source</a>
    </div>
  </footer>
`;
